import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CreditCard, ExternalLink, Sparkles } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PricingCards } from "@/pages/Pricing";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

const STATUS_LABEL = { active: "Attivo", free: "Gratuito", canceled: "Annullato", past_due: "Pagamento in sospeso", trialing: "Periodo di prova" };

export function Billing() {
  const { t } = useT();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["subscription"], queryFn: async () => (await api.get("/billing/subscription")).data });

  const portal = async () => {
    try { const res = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = res.data.url; }
    catch (e) { toast.error(e.response?.data?.detail || t("errors.generic")); }
  };
  const isPaid = user?.plan !== "free";
  const scheduled = Boolean(data?.cancel_at_period_end);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("billing.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("billing.subtitle")}</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="billing-current">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white">
                {isPaid ? <Sparkles className="h-6 w-6" /> : <CreditCard className="h-6 w-6" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg font-bold capitalize">{user?.plan}</span>
                  <Badge variant={scheduled ? "outline" : "secondary"} data-testid="subscription-status-badge" className={scheduled ? "border-amber-500 text-amber-600 dark:text-amber-400" : ""}>
                    {scheduled ? t("billing.cancelScheduled") : (STATUS_LABEL[data?.status] || data?.status)}
                  </Badge>
                </div>
                {data?.current_period_end && (
                  <p className="text-sm text-muted-foreground" data-testid="subscription-period-end">
                    {scheduled ? t("billing.activeUntil") : t("billing.renewsOn")} {formatDate(data.current_period_end)}
                  </p>
                )}
                {scheduled && (
                  <p className="text-sm text-amber-600 dark:text-amber-400" data-testid="cancel-scheduled-note">
                    {data?.current_period_end ? `${t("billing.cancelScheduledFor")} ${formatDate(data.current_period_end)}` : t("billing.canceled")}
                  </p>
                )}
              </div>
            </div>
            {isPaid && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={portal} data-testid="portal-btn" className="rounded-xl"><ExternalLink className="mr-2 h-4 w-4" />{t("billing.manage")}</Button>
              </div>
            )}
          </div>
        </div>

        <div>
          <h2 className="mb-5 font-display text-lg font-semibold">{isPaid ? t("billing.upgrade") : t("pricing.title")}</h2>
          <PricingCards />
        </div>
      </div>
    </DashboardLayout>
  );
}
